import type { FormValidationErrorMap } from '@luna/form2';

interface ErrorWhere {
    pointer: string;
    type: string;
    detail?: string;
}

interface ErrorMatch {
    name: string;
    message?: string;
    where: ErrorWhere;
}

const NEW_LINE_CHAR = '\n';

function normalizePointer(pointer: string): string {
    return pointer.replace(/^#\//, '');
}

type ErrorMatchWhereTypes<T extends readonly ErrorMatch[]> = T[number]['where']['type'];
type ErrorMatchWherePointers<T extends readonly ErrorMatch[]> = T[number]['where']['pointer'];
type ErrorMatchNames<T extends readonly ErrorMatch[]> = T[number]['name'];
type ErrorMatcher<T extends readonly ErrorMatch[]> = Record<
    ErrorMatchWhereTypes<T>,
    Record<ErrorMatchWherePointers<T>, ErrorMatch & { name: ErrorMatchNames<T> }>
>;
export type ErrorMatcherOf<T> = Record<
    string,
    Record<string, ErrorMatch & { name: keyof FormValidationErrorMap<T> }>
>;

export type ProblemError = ErrorWhere;

export function createErrorMatcher<
    T extends readonly ErrorMatch[] & Array<{ name: N; where: { type: E; pointer: P } }>,
    E extends string,
    P extends string,
    N extends string,
>(...matchers: T): ErrorMatcher<T> {
    return matchers.reduce((map, error) => {
        const type = error.where.type as ErrorMatchWhereTypes<T>;
        const pointer = normalizePointer(error.where.pointer) as ErrorMatchWherePointers<T>;

        if (!map[type]) {
            map[type] = {} as Record<
                ErrorMatchWherePointers<T>,
                ErrorMatch & { name: ErrorMatchNames<T> }
            >;
        }

        map[type][pointer] = error as ErrorMatch & { name: ErrorMatchNames<T> };

        return map;
    }, {} as ErrorMatcher<T>);
}

/**
 * Transforms RFC 7807 Problem Details errors into map of form errors through provided declarative configuration.
 *
 * Example usage:
 *
 * transformErrorsToFormValidationErrorMap<FormValues>({
 *        errors,
 *        matcher: createErrorMatcher(
 *             {
 *                 name: 'lunaFormFieldName1', // <- field name is checked against provided interface
 *                 message: t('i18n1'),
 *                 where: { type: 'error:george-addressbook/some-error1', pointer: '#/bankConnection' },
 *             },
 *            {
 *                 name: 'lunaFormFieldName2',
 *                 message: t('i18n2'),
 *                 where: { type: 'error:george-addressbook/some-error2, pointer: '#/accountNumber' },
 *             },
 *        )
 *  })
 */
export function transformErrorsToFormValidationErrorMap<T>({
    errors,
    matcher,
}: {
    errors: ErrorWhere[];
    matcher: ErrorMatcherOf<T>;
}): Partial<FormValidationErrorMap<T>> | undefined {
    const map = errors.reduce<Partial<FormValidationErrorMap<T>>>((result, error) => {
        const { pointer, type } = error;
        const normalizedPointer = normalizePointer(pointer);

        const match = matcher[type]?.[normalizedPointer];

        if (match) {
            result[match.name] = [result[match.name] ?? '', match.message ?? error.detail ?? '']
                .filter(Boolean)
                .join(NEW_LINE_CHAR);
        }

        return result;
    }, {});

    if (!Object.keys(map).length) {
        return undefined;
    }

    return map;
}
